import { format, parseISO } from 'date-fns';
import { cleanSlotRange, composeSlotLabel } from '../utils/slotName';

const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMonthLabel = (monthValue) => {
  try {
    const date = typeof monthValue === 'string' ? parseISO(monthValue.slice(0, 10)) : new Date(monthValue);
    return format(date, 'MMM yyyy');
  } catch {
    return String(monthValue || '');
  }
};

export const formatTimeSlot = (row) => {
  // GA-004: normalise possibly-doubled stored values to a clean range.
  if (row.time_slot) return cleanSlotRange(row.time_slot);
  if (row.timeSlot) return cleanSlotRange(row.timeSlot);
  const startRaw = row.slot_start_time || row.start_time;
  const endRaw = row.slot_end_time || row.end_time;
  if (startRaw && endRaw) {
    const start = String(startRaw).slice(0, 5);
    const end = String(endRaw).slice(0, 5);
    return `${start}-${end}`;
  }
  return 'N/A';
};

export const formatSlotLabel = (row) => {
  const timeSlot = formatTimeSlot(row);
  const slotName = row.configured_slot_name || row.slot_name || row.slotName;
  if (timeSlot === 'N/A') return slotName || 'N/A';
  return composeSlotLabel(slotName, timeSlot);
};

export function mapMonthlySummaryRows(rows = []) {
  return rows.map((row) => {
    const monthKey = String(row.month || '').slice(0, 7);
    return {
      month: formatMonthLabel(row.month),
      monthKey,
      totalSlots: Number(row.total_slots) || 0,
      uniqueServices: Number(row.unique_services) || 0,
    };
  });
}

export function mapMonthlyDetailRows(rows = []) {
  return rows.map((row) => ({
    id: row.id,
    date: row.date,
    timeSlot: formatTimeSlot(row),
    slotId: row.slot_id,
    slotName: row.configured_slot_name || row.slot_name,
    groundName: row.ground_name,
    serviceName: row.service_name,
    bookingDate: row.booking_date,
    bookingType: row.booking_type,
    bookedBy: row.booked_by,
    bookedByPhone: row.booked_by_phone,
    contactName: row.contact_name,
    contactPhone: row.contact_phone,
    teamDetails:
      row.team_a_name || row.team_b_name
        ? {
            teamA: row.team_a_name
              ? { name: row.team_a_name, captain: row.team_a_captain_name || '' }
              : null,
            teamB: row.team_b_name
              ? { name: row.team_b_name, captain: row.team_b_captain_name || '' }
              : null,
          }
        : null,
  }));
}

export function mapSlotPaymentRows(rows = []) {
  return rows.map((row) => ({
    slotKey: `${row.ground_name || 'ground'}-${row.service_name || 'service'}-${row.slot_id || row.slot_name || 'slot'}`,
    groundName: row.ground_name || 'Unknown Ground',
    serviceName: row.service_name || 'Unknown Service',
    slotId: row.slot_id || 'N/A',
    slotName: row.slot_name,
    timeSlot: formatTimeSlot(row),
    totalAmount: toNumber(row.total_amount),
    paidAmount: toNumber(row.paid_amount),
    pendingAmount: toNumber(row.pending_amount),
    bookingCount: Number(row.booking_count) || 0,
  }));
}

export function mapTransactionRows(rows = []) {
  return rows.map((row) => ({
    id: row.id,
    bookingId: row.booking_id,
    date: row.payment_date || row.created_at,
    groundName: row.ground_name || 'N/A',
    serviceName: row.service_name || 'N/A',
    slotId: row.slot_id || 'N/A',
    slotName: row.configured_slot_name || row.slot_name,
    timeSlot: formatTimeSlot(row),
    customerName: row.paid_by || 'N/A',
    customerPhone: row.mobile || 'N/A',
    amount: toNumber(row.amount),
    paymentMode: row.payment_mode || 'cash',
    transactionType: row.is_initial_advance ? 'Initial Advance' : 'Additional Payment',
    isInitialAdvance: Boolean(row.is_initial_advance),
  }));
}
